var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import Input from '@material-ui/core/Input';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import RemoveIcon from '@material-ui/icons/Remove';
import Skeleton from '@material-ui/lab/Skeleton';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import clsx from 'clsx';
import { useDirection, useFieldProps, useFormRefs, useTextFieldError, useForm } from '../hooks';
import FormHelperText from './FormHelperText';
import FormInputLabel from './FormInputLabel';
var FormNumberInput = function (_a) {
    var id = _a.id, description = _a.description, type = _a.type, dir = _a.dir, loading = _a.loading, helperText = _a.helperText, ac = _a.autoComplete, labelAdornment = _a.labelAdornment, _b = _a.classes, _c = _b === void 0 ? {} : _b, container = _c.container, labelContainer = _c.labelContainer, label = _c.label, maxLengthClass = _c.maxLength, helperTextClassName = _c.helperText, errorContainer = _c.errorContainer, inputClasses = __rest(_c, ["container", "labelContainer", "label", "maxLength", "helperText", "errorContainer"]), rest = __rest(_a, ["id", "description", "type", "dir", "loading", "helperText", "autoComplete", "labelAdornment", "classes"]);
    var innerRef = useFormRefs();
    var fieldProps = useFieldProps(id);
    var fieldError = useTextFieldError(id);
    var direction = useDirection();
    var form = useForm();
    var classes = useFormInputStyles();
    var reduce = useCallback(function () {
        if (form.values[id] === undefined) {
            form.setFieldValue(id, 0);
        }
        if (form.values[id] > 0) {
            form.setFieldValue(id, form.values[id] - 1);
        }
    }, [form.values[id]]);
    var increment = useCallback(function () {
        if (form.values[id] === undefined)
            form.setFieldValue(id, 1);
        else
            form.setFieldValue(id, form.values[id] + 1);
    }, [form.values[id]]);
    return (_jsxs("div", __assign({ className: clsx(classes.container, container) }, { children: [description && _jsx(FormInputLabel, { id: id, description: description, labelAdornment: labelAdornment, classes: { labelContainer: labelContainer, label: label } }, void 0),
            loading ? (_jsx(Skeleton, { width: "120px", variant: "rect", className: clsx(classes.input, classes.skeleton) }, void 0)) : (_jsxs(ButtonGroup, __assign({ className: clsx(classes.buttonGroupContainer, fieldError && classes.border) }, { children: [_jsx(Button, __assign({ onClick: reduce, className: classes.button }, { children: _jsx(RemoveIcon, { fontSize: 'small' }, void 0) }), void 0),
                    _jsx(Input, __assign({ id: id, type: type, disableUnderline: true, placeholder: '0', dir: dir || direction, className: classes.input, inputRef: function (el) { if (innerRef)
                            innerRef.current[id] = el; }, classes: inputClasses, autoComplete: 'off' }, fieldProps, rest), void 0),
                    _jsx(Button, __assign({ onClick: increment, className: classes.button }, { children: _jsx(AddIcon, { fontSize: 'small' }, void 0) }), void 0)] }), void 0)),
            helperText && _jsx(FormHelperText, { className: helperTextClassName, text: helperText }, void 0),
            _jsx(FormHelperText, { className: errorContainer, text: fieldError, error: true }, void 0)] }), void 0));
};
export var useFormInputStyles = makeStyles(function (theme) {
    var _a, _b, _c, _d;
    return ({
        container: {
            width: 'fit-content'
        },
        buttonGroupContainer: (_a = {
                border: "1px solid " + theme.palette.primary.main,
                borderRadius: '30px',
                width: 120
            },
            _a[theme.breakpoints.up(500)] = {
                height: 30
            },
            _a[theme.breakpoints.down(500)] = {
                height: 32
            },
            _a[theme.breakpoints.up(1600)] = {
                height: 45
            },
            _a),
        skeleton: (_b = {
                borderRadius: '50px !important'
            },
            _b[theme.breakpoints.up(500)] = {
                height: 31
            },
            _b[theme.breakpoints.down(500)] = {
                height: 30
            },
            _b[theme.breakpoints.up(1600)] = {
                height: 45
            },
            _b),
        input: {
            color: theme.palette.primary.main,
            display: 'flex',
            textAlign: 'center',
            fontFamily: 'Assistant-Regular',
            borderRadius: 5,
            padding: 'unset',
            border: '1px solid white',
            transition: 'background-color 0.5s ease',
            width: 'min-content',
            '&:hover, &:focus-within': {
                backgroundColor: '#ECECEC'
            },
            '& input , & textarea': (_c = {
                    borderRadius: 5,
                    textAlign: 'center'
                },
                _c[theme.breakpoints.up(500)] = {
                    padding: '4px 7px',
                    fontSize: 17
                },
                _c[theme.breakpoints.down(500)] = {
                    padding: '6px 7px',
                    fontSize: 22
                },
                _c[theme.breakpoints.up(1600)] = {
                    padding: '9px 10px',
                    fontSize: 23
                },
                _c),
            '& textarea': {
                lineHeight: 1.3
            }
        },
        border: (_d = {
                borderColor: theme.palette.error.main,
                borderStyle: 'solid'
            },
            _d[theme.breakpoints.up(500)] = {
                borderWidth: 1
            },
            _d[theme.breakpoints.down(500)] = {
                borderWidth: 1.5
            },
            _d),
        button: {
            border: 'none',
            borderRadius: '30px 0 0 30px',
            color: theme.palette.primary.main,
            fontFamily: 'Assistant-Bold',
            fontSize: '1.3rem',
            padding: '0px !important',
            '&:nth-of-type(2)': {
                borderRadius: '0 30px 30px 0'
            }
        }
    });
});
export default FormNumberInput;
//# sourceMappingURL=FormNumberInput.js.map